/*
* Report page
*/

import * as _ from 'lodash';
import * as React from 'react';
import * as WordWrap from 'word-wrap';

import * as AppUtilities from '../../../common/apputilities';
import { Formatters } from '../../../common/formatters';
import * as ReportLogic from '../../logic';
import * as ReportComponents from '../report-components';

export const Render = ({ registerFunc, refFunc, scrollToFunc, pageTitle = '', className = '', maxTableRowsPerPage = 0, pageConfiguration, pageCount, reportData, appText, ...rest }) => {

    const sectionName = ReportComponents.ReportSectionNames.projectedReserveExpenditures;
    const ReportPageWithRegister = ReportComponents.withPageRegistration(ReportComponents.ReportPageTableData, sectionName, registerFunc, refFunc, scrollToFunc);
    const getTableRowKey = (id, secName, tabName = 'data') => ReportComponents.getRowKey(id, secName, tabName);

    const { formData, association, inventory, currentOrder, currentOrderChart, percentFunded, yearlySummaryData } = reportData;
    const SHOW_SUBGROUP_TITLE: boolean = Formatters.getBool(currentOrderChart, 'groupComponents');

    // Configuration
    const maxComponentNameLenWrap = 50;
    const maxSubgroupNameLenWrap = 30;

    const chunkSize = 42;

    // Helpers
    const getEligibleExpenditures = (dataItems: any[]) => {
        let result = _(dataItems)
            .filter(i => {
                let ul = _.get(i, 'usefulLife', '0');
                if (ul === ReportLogic.usefulLifeOneTime) { ul = '1'; }

                if (!AppUtilities.isNumeric(ul) || i.bestCost <= 0) { return false; }

                return true;
            })
            .flatMap('expenditureMap')
            .filter(o => {
                return (o.hypoProjectedExpenseShare > 0) && (o.hypoRemainingLife === 0);
            })
            .value();

        return result;
    };

    const getProjectedExpenditures = (reserveExpenditures: any, curYear: any, eligibleExpenditures: any[], invItems: any[], assocInvSubgroups: any[]) => {
        let result = (reserveExpenditures > 0) ? _(eligibleExpenditures)
            .filter({ 'year': curYear })
            .orderBy('componentId', ['asc'])
            .map((projectedExpense, projectedExpenseInd) => {
                const expenseItem = _(invItems).find({ 'inventoryId': _.get(projectedExpense, 'inventoryId', 0) });

                const g = _.get(expenseItem, 'subgroup', '');
                // const sg = _.find(assocInvSubgroups, ['groupCode', g]);
                const sg = ((_.find(assocInvSubgroups, ['groupCode', g])) ? _.find(assocInvSubgroups, ['groupCode', g]) : _.find(assocInvSubgroups, ['groupCode', _.toString(_.parseInt(g))]));

                const expenseResult = {
                    _id: _.get(expenseItem, '_id', ''),
                    // componentId: _.get(expenseItem, 'componentId', 0),
                    componentId: (_.get(expenseItem, 'componentDecimal')) ? (_.toString(Formatters.getInt(expenseItem, 'componentId') + '.' + Formatters.getStr(expenseItem, 'componentDecimal'))) : Formatters.getInt(expenseItem, 'componentId'),
                    componentName: _.get(expenseItem, 'componentName', 0),
                    inventoryId: _.get(expenseItem, 'inventoryId', 0),
                    projectedCost: (_.get(projectedExpense, 'projectedExpenseShare')) ? (_.get(projectedExpense, 'projectedExpenseShare')) : (_.get(projectedExpense, 'hypoProjectedExpenseShare')),
                    subgroup: (sg) ? sg : '',
                    yearNum: curYear,
                };

                return expenseResult;
            })
            .value() : [];

        return result;
    };

    const transformDataItems = (dataItems: any[]) => {
        const eligibleExpenditures = getEligibleExpenditures(inventory);

        const transformed = _(dataItems)
            .map((yearData, yearInd) => {
                const curYear = _.get(yearData, 'year', 0);

                const projectedExpenditures = getProjectedExpenditures(yearData.h_re, curYear, eligibleExpenditures, inventory, association.inventorySubgroups);

                const totalPerAnnum = _.sumBy(projectedExpenditures, 'projectedCost');
                const firstItem = _.head(projectedExpenditures);

                const yearRecord = _.merge(
                    {},
                    {
                        _id: '',
                        componentId: '',
                        componentName: appText('page.projected-expenditures.no-expenditures-projected'), // 'No Expenditures Projected',
                        projectedCost: 0,
                        showTotalPerAnnum: false,
                        totalPerAnnum: totalPerAnnum,
                        year: yearData.year,
                        yearNum: curYear,
                        wrappedItemRow: false,
                        wrappedItemRow_sort: 0,
                    },
                    firstItem
                );

                const yearChildRecords = _(projectedExpenditures)
                    .tail()
                    .map((item, itemInd) => {
                        return {
                            _id: _.get(item, '_id', ''),
                            componentId: _.get(item, 'componentId', ''),
                            componentName: _.get(item, 'componentName', ''),
                            projectedCost: _.get(item, 'projectedCost', 0),
                            subgroup: _.get(item, 'subgroup', ''),
                            showTotalPerAnnum: false,
                            totalPerAnnum: totalPerAnnum,
                            year: '',
                            yearNum: curYear,
                            wrappedItemRow: false,
                            wrappedItemRow_sort: 0,
                            inventoryId: '',
                        };
                    })
                    .value();

                const yearChildRecordsSort = _.orderBy(yearChildRecords, [c => Formatters.getInt(c, 'componentId'), c => Formatters.getStr(c, 'componentName').toLowerCase()], ['asc', 'asc']);

                const yearResults = _(yearRecord)
                    .concat(yearChildRecordsSort)
                    .reverse()
                    .set('[0].showTotalPerAnnum', true)
                    .set('[0].className', 'pdf-data-table-projected-reserve-expenditures-year-row')
                    .reverse()
                    .value();

                let yearResultsExpanded = _.stubArray();
                _.forEach(yearResults, (r) => {
                    let rItem = _.cloneDeep(r);

                    yearResultsExpanded.push(rItem);
                });

                return yearResultsExpanded;
            })
            .flatten()
            .value();

        return transformed;
    };

    const buildRow = (data, dataInd, tableName: string = 'tbl', showSubgroupTitle: boolean = false) => {
        const isDisplayItemOnly = Formatters.getBool(data, 'wrappedItemRow');

        let result = (
            <tr key={getTableRowKey(`${Formatters.getStr(data, 'yearNum')}-${Formatters.getStr(data, '_id')}-${dataInd}`, sectionName, tableName)} className={_.get(data, 'className', '')}>
                <td className="text-center">{(!isDisplayItemOnly) ? Formatters.getStr(data, 'year') : undefined}</td>
                {(showSubgroupTitle) ? (!isDisplayItemOnly) ? <td className="text-left">{Formatters.getStr(data, 'subgroup.title')}</td> : undefined : undefined}
                <td className="text-center">{(!isDisplayItemOnly) ? Formatters.getStr(data, 'componentId') : <span />}</td>
                <td className="text-left">{Formatters.getStr(data, 'componentName')}</td>
                <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={Formatters.getFloat(data, 'projectedCost', 0)} decimals={0} /> : <span />}</td>
                <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency hidden={!Formatters.getBoolTrue(data, 'showTotalPerAnnum')} value={Formatters.getFloat(data, 'totalPerAnnum', 0)} decimals={0} /> : <span />}</td>
            </tr>
        );

        return result;
    };

    const buildRows = (dataItems: any[], showSubgroupTitle: boolean = false) => {
        const rows = _(dataItems)
            .map((item, ind, arr) => buildRow(item, ind, 'buildRows-table-pre', showSubgroupTitle))
            .value();

        return rows;
    };

    /* NEW */
    const transformedInvItems = transformDataItems(yearlySummaryData);
    const tableRows = buildRows(transformedInvItems, SHOW_SUBGROUP_TITLE);

    const rowWouldWrap = (row: any) => {
        const nonDisplayedSubgroupBuffer = (SHOW_SUBGROUP_TITLE) ? 0 : 25;
        return (row.props.children.length === 6 &&
            ((row.props.children[1] && (_.size(row.props.children[1].props.children) > maxSubgroupNameLenWrap)) ||
                (row.props.children[3] && (_.size(row.props.children[3].props.children) > (maxComponentNameLenWrap + nonDisplayedSubgroupBuffer)))));

    };

    const groupRowsToPages = (rows: any) => {
        let res: Array<any[]> = Array<any[]>();
        let rowCount = 0;
        let currentChunk: any = [];
        _.map(rows, (row, rowInd) => {
            if (rowWouldWrap(row)) {
                if (SHOW_SUBGROUP_TITLE) {
                    rowCount += 1; // rowCount + 0.5;
                } else {
                    rowCount += 0.5;
                }
            }
            if (rowCount < chunkSize) {
                currentChunk.push(row);
                rowCount++;
                if (Number(rowInd) === _.size(rows) - 1) {
                    res[res.length] = [[...currentChunk]];
                }
            } else {
                res[res.length] = [[...currentChunk]];
                currentChunk.length = 0;
                rowCount = 0;
                if (rowWouldWrap(row)) {
                    if (SHOW_SUBGROUP_TITLE) {
                        rowCount += 1; // rowCount + 0.5;
                    } else {
                        rowCount += 0.5;
                    }
                }
                currentChunk.push(row);
                rowCount++;
            }
        });
        return res;
    };
    const pages = groupRowsToPages(tableRows);

    const pagesNew = _(pages)
        .map((chunkItems, chunkInd, chunkInputArr) => {
            const keyPrefix = `chunkInd-projresexp-${chunkInd}`;

            const headRow = (
                <tr>
                    <th className="text-center col-pre-year">{appText('page.projected-expenditures.grid-column-headers.year')}</th>
                    {(SHOW_SUBGROUP_TITLE) ? <th className="text-left col-pre-subgroup">{appText('page.projected-expenditures.grid-column-headers.subgroup')}</th> : undefined}
                    <th className="text-center col-pre-compid">{appText('page.projected-expenditures.grid-column-headers.component-id')}</th>
                    <th className="text-left col-pre-compname">{appText('page.projected-expenditures.grid-column-headers.component-name')}</th>
                    <th className="text-right col-pre-projcost">{appText('page.projected-expenditures.grid-column-headers.projected-cost')}</th>
                    <th className="text-right col-pre-tpa">{appText('page.projected-expenditures.grid-column-headers.total-per-annum')}</th>
                </tr>
            );

            const chunkPage = (
                <div key={getTableRowKey(`projresexp-page-keyPrefix-${keyPrefix}`, sectionName, 'tbl-projresexp-chunk-page')} className="pdf-data-table-projected-reserve-expenditures-chunkpage">
                    <ReportPageWithRegister
                        className={className}
                        classNameTable="pdf-data-table-projected-reserve-expenditures"
                        footerData={reportData}
                        pageIndex={chunkInd}
                        pageTitle={pageTitle}
                        showFooter={true}
                        tbodyRows={chunkItems}
                        theadRow={headRow}
                        totalPageCount={pageCount}
                    />
                </div>
            );

            return chunkPage;
        })
        .value();

    return (
        <div className="pdf-data-table-projected-reserve-expenditures-pages-wrapper">
            {pagesNew}
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/report/pdf/pages/report-page-projected-reserve-expenditures.tsx